import { Box, MenuItem, Select, Tooltip } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { ReactElement, ReactNode, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

const c = tokens.color;

export interface PaloTableFootProps {
    count: number;
    /** 1-based. */
    page: number;
    limit: number;
    rowsPerPageOptions?: number[];
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
    /**
     * How many rows the backend lets a list page through. The API refuses any
     * page beyond its window — page 201 of 50 rows failed while the pager
     * offered all 1,458 (#2184). Null for a list that is not bound by it.
     */
    maxResults?: number | null;
    /** What the rows shown add up to, beside the range — "Summe netto …". */
    summary?: ReactNode;
}

/**
 * The backend's pagination window for lists read from the search engine.
 *
 * It is `Config::MAX_SEARCH_RESULT_WINDOW` of the backend and has to carry the
 * same figure: lower and the pager hides pages the server would answer — of
 * 72,871 invoices only the first 10,000 were reachable and the list stopped at
 * page 200 (#2210, R-17); higher and it offers pages the server refuses.
 */
export const PALO_MAX_RESULTS = 250000;

/** The last page a list can actually reach with the given row count. */
export const reachablePageCount = (
    count: number,
    limit: number,
    maxResults: number | null = PALO_MAX_RESULTS
): number => {
    const pageCount = Math.max(1, Math.ceil(count / limit));

    return null === maxResults ? pageCount : Math.min(pageCount, Math.max(1, Math.floor(maxResults / limit)));
};

/** A gap in the page run, rendered as the prototype's ellipsis button. */
const GAP = 'gap';

/**
 * Page numbers around the current one, with the first and last always present
 * and a gap where the run breaks: 1 … 7 8 9 … 26.
 */
const pageRun = (page: number, pageCount: number): (number | typeof GAP)[] => {
    if (pageCount <= 7) {
        return Array.from({ length: pageCount }, (_, index) => index + 1);
    }

    const around = [page - 1, page, page + 1].filter(p => p > 1 && p < pageCount);
    const run: (number | typeof GAP)[] = [1];

    if (around[0] !== undefined && around[0] > 2) {
        run.push(GAP);
    }
    run.push(...around);
    if (around[around.length - 1] !== undefined && (around[around.length - 1] as number) < pageCount - 1) {
        run.push(GAP);
    }
    run.push(pageCount);

    return run;
};

const pageButtonSx = (active: boolean) => ({
    minWidth: 25,
    height: 25,
    px: '5px',
    border: '1px solid transparent',
    borderRadius: `${tokens.radius.small}px`,
    background: active ? c.accent : 'transparent',
    color: active ? '#fff' : 'inherit',
    fontWeight: active ? 600 : 400,
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': { background: active ? c.accent : c.neutralSoft },
    '&:disabled': { opacity: 0.4, cursor: 'default' },
});

/**
 * The prototype's `.tbl-foot`: rows-per-page, the displayed range and a compact
 * numeric pager, in place of MUI's wide pagination bar.
 */
export const PaloTableFoot = ({
    count,
    page,
    limit,
    rowsPerPageOptions = [25, 50, 100],
    onPageChange,
    onLimitChange,
    maxResults = PALO_MAX_RESULTS,
    summary,
}: PaloTableFootProps): ReactElement => {
    // A list that runs on a size of its own — the import runs on 30 — must find
    // that size in the list, or the box beside "rows" comes up empty.
    const options = rowsPerPageOptions.includes(limit)
        ? rowsPerPageOptions
        : [...rowsPerPageOptions, limit].sort((left, right) => left - right);
    const pageCount = reachablePageCount(count, limit, maxResults);
    const truncated = pageCount < Math.ceil(count / limit);
    const current = Math.min(Math.max(1, page), pageCount);
    const from = count === 0 ? 0 : (current - 1) * limit + 1;
    const to = Math.min(count, current * limit);

    return (
        <Box
            sx={{
                height: 36,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                px: '11px',
                borderTop: `1px solid ${c.line}`,
                backgroundColor: c.surfaceAlt,
                fontSize: 12,
                color: c.ink2,
            }}
        >
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FormattedMessage id="app.table.rows" defaultMessage="Zeilen:" />
                <Select
                    variant="standard"
                    disableUnderline={true}
                    value={limit}
                    onChange={event => onLimitChange(Number(event.target.value))}
                    sx={{ fontSize: 12, fontWeight: 600, color: c.ink, '& .MuiSelect-select': { py: 0 } }}
                >
                    {options.map(option => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                <FormattedNumber value={from} />–<FormattedNumber value={to} />{' '}
                <FormattedMessage id="app.table.of" defaultMessage="von" /> <FormattedNumber value={count} />
            </Box>

            {summary && (
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', color: c.ink }}>
                    {summary}
                </Box>
            )}

            {truncated && (
                <Tooltip
                    title={
                        <FormattedMessage
                            id="app.table.truncatedHint"
                            defaultMessage="Geblättert werden kann bis Zeile {max}. Ältere Einträge über einen Filter, etwa den Zeitraum, oder mit umgedrehter Sortierung."
                            values={{ max: <FormattedNumber value={pageCount * limit} /> }}
                        />
                    }
                >
                    <Box component="span" sx={{ color: c.ink3 }}>
                        <FormattedMessage
                            id="app.table.truncated"
                            defaultMessage="ältere Einträge über Filter oder Sortierung"
                        />
                    </Box>
                </Tooltip>
            )}

            <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Box
                    component="button"
                    type="button"
                    aria-label="Vorherige Seite"
                    disabled={current <= 1}
                    onClick={() => onPageChange(current - 1)}
                    sx={pageButtonSx(false)}
                >
                    ‹
                </Box>
                {pageRun(current, pageCount).map((entry, index) =>
                    entry === GAP ? (
                        <Box key={`gap-${index}`} component="span" sx={{ ...pageButtonSx(false), cursor: 'default' }}>
                            …
                        </Box>
                    ) : (
                        <Box
                            key={entry}
                            component="button"
                            type="button"
                            onClick={() => onPageChange(entry)}
                            sx={pageButtonSx(entry === current)}
                        >
                            {entry}
                        </Box>
                    )
                )}
                <Box
                    component="button"
                    type="button"
                    aria-label="Nächste Seite"
                    disabled={current >= pageCount}
                    onClick={() => onPageChange(current + 1)}
                    sx={pageButtonSx(false)}
                >
                    ›
                </Box>
            </Box>
        </Box>
    );
};

/** Rows shown at once in a table that is paged on the client. */
export const PALO_ROWS_PER_PAGE = 50;

export interface PaloTablePage {
    /** 1-based. */
    page: number;
    limit: number;
    /** Index of the first row shown, into the full list. */
    offset: number;
    /** Whether the table is long enough to need the foot at all. */
    paged: boolean;
    setPage: (page: number) => void;
    setLimit: (limit: number) => void;
}

/**
 * Client-side paging for a table that holds all its rows already.
 *
 * A position table used to scroll inside itself once it grew past its box,
 * which put a scrollbar inside the one the mask has. ERPNext pages its rows
 * instead, and so do we: what is on screen is a whole page, and the table is
 * exactly as tall as that page (#2208).
 */
export const usePaloTablePage = (count: number, rowsPerPage: number = PALO_ROWS_PER_PAGE): PaloTablePage => {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(rowsPerPage);
    const pageCount = Math.max(1, Math.ceil(count / limit));
    // Deleting the last rows must not leave the table on a page past the end.
    const current = Math.min(page, pageCount);

    return {
        page: current,
        limit,
        offset: (current - 1) * limit,
        paged: count > limit,
        setPage,
        setLimit: (next: number) => {
            setLimit(next);
            setPage(1);
        },
    };
};
